<template>
  <template v-if="!showPreview">
    <!-- Teleport header actions to main header -->
    <Teleport v-if="!showPreview && isMounted" to="#additional-right-header-content">
      <div class="row items-center q-gutter-sm">
        <Search
          :suggestions="searchSuggestions"
          search-button-title="Search Posts"
          close-button-title="Close Search"
          :placeholder="$t('Search posts, authors, keywords...')"
          @update:modelValue="handleSearchChanged"
          @opene="handleSearchOpened"
          @close="handleSearchClosed"
        />
      </div>
    </Teleport>

    
    <q-page class="social-feed-container">

      <!-- Categories List -->
      <q-scroll-area style="flex: 1 1 1px;">
        <div class="q-pa-md" style="max-width: 100vw;">
          <div class="q-gutter-md">
            <social-feed-card
              v-for="feedItem in filteredFeedItems" 
              :key="feedItem.id"
              :feed-item="feedItem"
              :expanded="expandedIds.has(feedItem.id)"
              @toggle-item="toggleFeedItem"
            />
          </div>

          <!-- No Results Message -->
          <div v-if="filteredFeedItems.length === 0 && (searchQuery || selectedPlatform !== 'all')" class="text-center q-pa-xl">
            <div class="text-h6 text-grey-6 q-mb-sm">{{$t('No posts found')}}</div>
            <div class="text-body2 text-grey-5">
              <span v-if="searchQuery">{{$t('No posts match your search for')}} "{{ searchQuery }}"</span>
              <span v-else>{{$t('No posts match your current filters')}}</span>
            </div>
          </div>

          <!-- Loading More -->
          <div v-if="loading" class="text-center q-pa-md">
            <q-spinner-dots color="brown-6" size="2em" />
            <div class="text-grey-6 q-mt-sm">{{$t('Loading more posts')}}...</div>
          </div>
        </div>
      </q-scroll-area>
    </q-page>
  </template>
  <template v-else>
    <!-- Preview layout: show in a small container with a few items -->
    <!-- <div ref="previewContainer" class="social-feed-preview-container q-pa-sm"> -->
    <!-- fit -->
    <div ref="previewContainer" class="preview-container col q-pa-xs q-gutter-sm">
          <social-feed-card
            v-for="(feedItem, index) in previewFeedItems"
            :key="feedItem.id"
            ref="feedCards"
            :feed-item="feedItem"
            :expanded="false"
            :show-preview="true"
            :class="{ 'card-hidden': isMeasuring && index >= countFitItems }"
            @click="setActiveView('social-feed')"
          />
    </div>
  </template>
</template>


<script setup>
import { ref, computed, getCurrentInstance, nextTick, onMounted, onBeforeUnmount, watch } from 'vue'
import { useNavigation } from '@/common/composables/useNavigation.js'

import SocialFeedCard from '@/common/components/social-feed/social-feed-card.vue'
import Search from '@/common/components/ui/search.vue'
import Filter from '@/common/components/filter.vue'
import { is } from 'quasar'

const { setActiveTab, setActiveView } = useNavigation()

const props = defineProps({
  showPreview: {
    type: Boolean,
    default: false
  },
})

// State
const expandedIds = ref(new Set())
const searchQuery = ref('')
const selectedPlatform = ref('all')
const feedData = ref([])
const loading = ref(false)

let previewResizeObserver = null;
const previewContainer = ref(null)
const feedCards = ref([])
const previewHeight = ref(0)
const countFitItems = ref(0)
const isMeasuring = ref(false)     // render hidden items only while measuring
const isMounted = ref(false);

// Current component instance to access global methods
const { proxy } = getCurrentInstance()

// Platform options
const platformOptions = ref([
  { label: 'All Platforms', value: 'all' },
  // { label: 'Twitter', value: 'twitter' },
  { label: 'LinkedIn', value: 'linkedin' },
  { label: 'Bluesky', value: 'bluesky' },
  { label: 'Zenodo', value: 'zenodo' },
  { label: 'Zotero', value: 'zotero' },
  // TODO: add project news

])

// Search suggestions
const searchSuggestions = ref([
  {
    id: 'precision-agriculture',
    text: 'precision agriculture',
    description: 'Posts about precision farming techniques'
  },
  {
    id: 'soil-analysis',
    text: 'soil analysis',
    description: 'Posts about soil testing and analysis'
  },
  {
    id: 'sustainable-farming',
    text: 'sustainable farming',
    description: 'Posts about sustainable agriculture practices'
  },
  {
    id: 'crop-monitoring',
    text: 'crop monitoring',
    description: 'Posts about crop health and monitoring'
  },
  {
    id: 'research-papers',
    text: 'research papers',
    description: 'Academic research and publications'
  },
  {
    id: 'farm-technology',
    text: 'farm technology',
    description: 'Posts about agricultural technology'
  }
])

// Lifecycle hooks
onMounted(async () => {
  previewResizeObserver = new ResizeObserver(updatePreviewHeight);
  if (previewContainer.value) {
    previewResizeObserver.observe(previewContainer.value);
  }
  updatePreviewHeight();
  await loadSocialFeedData();
  // await recomputeCountFitItems();
  isMounted.value = true;
})

onBeforeUnmount(() => {
  if (previewResizeObserver && previewContainer.value) {
    previewResizeObserver.unobserve(previewContainer.value);
  }
})




const snakeToPascal = (s) => {
  return s
      .replaceAll('_', ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase())
      .replace(/\s+/g, '');
};

const snakeToCamel = (s) => {
  const sPascal = snakeToPascal(s);
  return sPascal.charAt(0).toLowerCase() + sPascal.slice(1);
};

const getRandomId = (platform) => {
  const randomPart = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID().slice(-10)
      : Math.random().toString(36).slice(2, 2 + 10);
  return `${platform}_${Date.now()}_${randomPart}`;
};

const loadSocialFeedData = async () => {
  loading.value = true
  feedData.value = []
  try {
    const apiData = await proxy.get('SocialFeed/GetAllPlatforms', null, true);
    const attributes = (apiData?.attributes ?? []).map((attribute) =>
      snakeToCamel(attribute.name),
    );
    const rowValues = apiData?.data ?? [];
    feedData.value = rowValues.map(rowValue => {
      const item = Object.fromEntries(attributes.map((key, i) => [key, rowValue[i]]));
      return {
        ...item,
        id: item.id || getRandomId(item.platform),
        keywords: item.keywords || [],
        facets: JSON.parse(item.facets || '[]'),
      };
    });
  } catch (error) {
    console.warn('Failed to load social feed data from API:', error)
  } finally {
    loading.value = false
  }
}

const elementInnerHeight = (element) => {
  if (element == null) {
    return 0;
  }
  const height = element.clientHeight || 0;
  const computedStyle = getComputedStyle(element);
  const paddingTop = Number.parseFloat(computedStyle.paddingTop) || 0;
  const paddingBottom = Number.parseFloat(computedStyle.paddingBottom) || 0;
  return height - paddingTop - paddingBottom;
};

const updatePreviewHeight = () => {
  if (!previewContainer.value) {
    return;
  }
  if (isMeasuring.value) {
    return;
  }
  previewHeight.value = elementInnerHeight(previewContainer.value);
  return;
}

const elementOuterHeight = (element) => {
  if (element == null) {
    return 0;
  }
  const height = element.offsetHeight || 0;
  const computedStyle = getComputedStyle(element);
  const marginTop = Number.parseFloat(computedStyle.marginTop) || 0;
  const marginBottom = Number.parseFloat(computedStyle.marginBottom) || 0;
  return height + marginTop + marginBottom;
};

const recomputeCountFitItems = async () => {
  isMeasuring.value = true
  await nextTick() // ensure DOM & refs are ready
  let cardsHeightSum = 0;
  let i = 0;
  console.log('🧮 Recomputing countFitItems, previewHeight=', previewHeight.value, 'cards=', feedCards.value.length)
  for (; i < feedCards.value.length; i++) {
    const cardHeight = elementOuterHeight(feedCards.value[i].$el)
    cardsHeightSum += cardHeight
    // Dopusti da zadnja kartica viri do 30% svoje visine
    if (cardsHeightSum > previewHeight.value + cardHeight * 0.3) {
      break
    }
    // if (cardsHeightSum >= previewHeight.value) {
    //   break
    // }
    console.log(`  item ${i}: height=${cardHeight}, cumulative=${cardsHeightSum}`)
    
  }
  countFitItems.value = i
  console.log('🧮 Recomputed countFitItems:', countFitItems.value, 'items fit in height', previewHeight.value)
  await nextTick() // ensure items are re-rendered
  isMeasuring.value = false
}

// Computed properties

const sortedFeedItems = computed(() => {
  const items = feedData.value
  // Sort by timeCreated date (newest first)
  items.sort((a, b) => new Date(b.timeCreated) - new Date(a.timeCreated))
  return items
})

// Computed property for filtered feed items
const filteredFeedItems = computed(() => {
  let items = sortedFeedItems.value;

  // Filter by platform
  if (selectedPlatform.value !== 'all') {
    items = items.filter(item => item.platform === selectedPlatform.value);
  }

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    items = items.filter(item => {
      const contentMatch = item.content.toLowerCase().includes(query)
      const authorMatch = item.author.toLowerCase().includes(query)
      return contentMatch || authorMatch
    })
  }

  return items
})

const topRecentFeedItems = computed(() => {
  // const maxNumberOfItems = 4;
  const maxNumberOfItems = 10;
  const items = sortedFeedItems.value;
  const resultItems = [];
  const platformCounts = {};

  for (const item of items) {
    const platform = item.platform;
    platformCounts[platform] = (platformCounts[platform] || 0) + 1;

    // if (platformCounts[platform] <= 2) {
    if (platformCounts[platform] <= 4) {
      resultItems.push(item);
    }
  }

  return resultItems.slice(0, maxNumberOfItems);
});

const previewFeedItems = computed(() => {
  // return [];
  const items = topRecentFeedItems.value;
  // return items;
  if (isMeasuring.value) {
    return items;
  }

  return items.slice(0, countFitItems.value);
});

// Watchers

watch([previewHeight, topRecentFeedItems], async () => {
  if (previewHeight.value === 0) {
    return;
  } 
  if (topRecentFeedItems.value.length === 0) {
    return;
  }
  await recomputeCountFitItems();
}, { flush: 'post' })


// Event handlers

const toggleFeedItem = (feedItemId) => {
  if (expandedIds.value.has(feedItemId)) {
    expandedIds.value.delete(feedItemId)
  } else {
    expandedIds.value.add(feedItemId)
  }
}


// Search event handlers
const handleSearchChanged = (query) => {
  searchQuery.value = query
}

const handleSearchOpened = () => {
  // Handle search opened
}

const handleSearchClosed = () => {
  // Handle search closed
}

// Filter event handlers
const handlePlatformChanged = (platform) => {
  selectedPlatform.value = platform
}

const handleFilterOpened = () => {
  // Handle filter opened
}

const handleFilterClosed = () => {
  // Handle filter closed
}

</script>

<style scoped>
.social-feed-container {
  width: 100%;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
}

.preview-container {
  /* width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column; */
  overflow: hidden;
}

.card-hidden {
  visibility: hidden;
  pointer-events: none;
  opacity: 0;
}
</style>